import { useEffect } from 'react'

const randomBetween = (min, max) => min + Math.random() * (max - min)
const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

function intersection(a, b) {
  const x = Math.max(a.x, b.x)
  const y = Math.max(a.y, b.y)
  const right = Math.min(a.x + a.width, b.x + b.width)
  const bottom = Math.min(a.y + a.height, b.y + b.height)
  return right > x && bottom > y ? { x, y, width: right - x, height: bottom - y } : null
}

function captionBounds(item) {
  return { x: item.x + item.captionX, y: item.y + item.captionY,
    width: item.captionWidth, height: item.captionHeight }
}

function blockedRegion(item, others) {
  for (let a = 0; a < others.length; a++) {
    for (let b = a + 1; b < others.length; b++) {
      const pair = intersection(others[a], others[b])
      if (pair && intersection(item, pair)) return pair
    }
  }
  for (const other of others) {
    const caption = captionBounds(other)
    if (intersection(captionBounds(item), caption)) return { ...caption, captionOnly: true }
  }
  return null
}

function escapeDirection(item, pair, padding = 12) {
  const own = pair.captionOnly ? captionBounds(item) : item
  const exits = [
    { x: pair.x - own.width - padding - own.x, y: 0 },
    { x: pair.x + pair.width + padding - own.x, y: 0 },
    { x: 0, y: pair.y - own.height - padding - own.y },
    { x: 0, y: pair.y + pair.height + padding - own.y },
  ].filter(exit => item.x + exit.x >= item.minX && item.x + exit.x <= item.maxX &&
    item.y + exit.y >= item.minY && item.y + exit.y <= item.maxY)
  return exits.sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y))[0]
}

export default function useFloatingArtwork(ref) {
  useEffect(() => {
    const container = ref.current
    if (!container) return

    const figures = [...container.querySelectorAll('.line-drawing')]
    const main = container.closest('.hero-main')
    const hero = container.closest('.hero')
    const fillLayer = main.querySelector('.drawing-fills')
    const fills = [...(fillLayer?.querySelectorAll('img[data-model]') || [])]
    const fillByProduct = new Map(fills.map(fill => [fill.dataset.model, fill]))
    const artworkLayers = [...figures, ...fills]
    const captions = figures.map(figure => figure.querySelector('.drawing-caption'))
    const captionWidths = captions.map(caption => caption.style.width)
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let items = []
    let frame = null
    let lastTime = null
    let visible = true
    let speed = 10

    function chooseTarget(item) {
      const minimumLeg = Math.min(40, Math.max(item.maxX - item.minX, item.maxY - item.minY) * 0.4)
      for (let attempt = 0; attempt < 12; attempt++) {
        item.targetX = randomBetween(item.minX, item.maxX)
        item.targetY = randomBetween(item.minY, item.maxY)
        if (Math.hypot(item.targetX - item.x, item.targetY - item.y) >= minimumLeg) break
      }
      item.nextTarget = randomBetween(8, 18)
    }

    function paint(item) {
      const transform = `translate3d(${item.x - item.startX}px, ${item.y - item.startY}px, 0)`
      item.element.style.transform = transform
      if (item.fill) item.fill.style.transform = transform
    }

    function stop() {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = null
      lastTime = null
    }

    function canMove() {
      return !reducedMotion.matches && !document.hidden && visible && items.length > 0
    }

    function tick(time) {
      frame = null
      if (!canMove()) {
        lastTime = null
        return
      }
      const dt = lastTime === null ? 0 : Math.min((time - lastTime) / 1000, 0.05)
      lastTime = time
      for (const item of items) {
        item.nextTarget -= dt
        if (item.nextTarget <= 0 || Math.hypot(item.targetX - item.x, item.targetY - item.y) < 2) chooseTarget(item)
        let steerX = (item.targetX - item.x) * 0.35
        let steerY = (item.targetY - item.y) * 0.35
        const others = items.filter(other => other !== item)
        // Only a third footprint is diverted; ordinary pairwise overlaps remain free.
        for (const horizon of [0.5, 1, 2.5]) {
          const predicted = others.map(other => ({ ...other,
            x: clamp(other.x + other.vx * horizon, other.minX, other.maxX),
            y: clamp(other.y + other.vy * horizon, other.minY, other.maxY),
          }))
          const future = { ...item,
            x: clamp(item.x + item.vx * horizon, item.minX, item.maxX),
            y: clamp(item.y + item.vy * horizon, item.minY, item.maxY),
          }
          const pair = blockedRegion(future, predicted)
          if (!pair) continue
          const exit = escapeDirection(item, pair)
          if (exit) {
            const length = Math.hypot(exit.x, exit.y)
            steerX = exit.x / length * speed
            steerY = exit.y / length * speed
            item.targetX = clamp(item.x + exit.x, item.minX, item.maxX)
            item.targetY = clamp(item.y + exit.y, item.minY, item.maxY)
            item.nextTarget = randomBetween(4, 8)
          } else {
            steerX = -item.vx
            steerY = -item.vy
          }
          break
        }
        const length = Math.hypot(steerX, steerY)
        const limit = speed
        if (length > limit) {
          steerX *= limit / length
          steerY *= limit / length
        }
        const easing = 1 - Math.exp(-dt * 1.4)
        item.vx += (steerX - item.vx) * easing
        item.vy += (steerY - item.vy) * easing
        let x = clamp(item.x + item.vx * dt, item.minX, item.maxX)
        let y = clamp(item.y + item.vy * dt, item.minY, item.maxY)
        // Sequential admission preserves the no-triple invariant between predictions.
        const collision = blockedRegion({ ...item, x, y }, others)
        if (collision) {
          let low = 0
          let high = 1
          for (let attempt = 0; attempt < 12; attempt++) {
            const fraction = (low + high) / 2
            const candidate = { ...item,
              x: item.x + (x - item.x) * fraction,
              y: item.y + (y - item.y) * fraction,
            }
            if (blockedRegion(candidate, others)) high = fraction
            else low = fraction
          }
          x = item.x + (x - item.x) * low
          y = item.y + (y - item.y) * low
          const exit = escapeDirection(item, collision)
          if (exit) {
            item.targetX = clamp(item.x + exit.x, item.minX, item.maxX)
            item.targetY = clamp(item.y + exit.y, item.minY, item.maxY)
          } else chooseTarget(item)
          item.vx *= -0.5
          item.vy *= -0.5
        }
        if (x === item.minX || x === item.maxX) item.vx *= 0.8
        if (y === item.minY || y === item.maxY) item.vy *= 0.8
        item.x = x
        item.y = y
        paint(item)
      }
      frame = requestAnimationFrame(tick)
    }

    function updateRunning() {
      container.querySelectorAll('.drawing-description > span').forEach(caption => {
        caption.style.animationPlayState = document.hidden || !visible ? 'paused' : 'running'
      })
      if (canMove()) {
        if (frame === null) frame = requestAnimationFrame(tick)
      } else stop()
    }

    function measure() {
      stop()
      artworkLayers.forEach(figure => {
        figure.style.removeProperty('transform')
        figure.style.removeProperty('will-change')
      })
      container.style.removeProperty('--drawing-scale')
      fillLayer?.style.removeProperty('--drawing-scale')
      items = []
      const bounds = container.getBoundingClientRect()
      const mainBounds = main.getBoundingClientRect()
      const heroBounds = hero.getBoundingClientRect()
      if (!bounds.width || !bounds.height) return

      const top = Math.max(bounds.top, heroBounds.top) - bounds.top
      const bottom = Math.min(bounds.bottom, mainBounds.bottom) - bounds.top
      const mobile = bounds.width <= 600
      const staggered = bounds.width > 1200
      const planeHeight = bottom - top
      const sizingWidth = Math.min(bounds.width, 1920)
      const layoutProgress = clamp((sizingWidth - 390) / (1920 - 390), 0, 1)
      const sizingHeight = mobile ? planeHeight : Math.min(planeHeight, 600)
      const compressed = planeHeight < 220
      captions.forEach((caption, index) => {
        caption.style.width = compressed && !mobile ? 'max(100%, 90px)' : captionWidths[index]
      })
      const referenceHeight = Math.max(sizingHeight, mobile ? 420 : bounds.width <= 900 ? 440 : 370)
      const minimumGap = mobile ? 8 : 18
      let horizontalReserve = mobile || staggered ? 5 : 12
      const verticalDrift = mobile ? 3 : 14
      const edge = mobile ? 4 : 12
      speed = 5 + 5 * layoutProgress
      let rows = (mobile ? [[1, 3, 6], [0, 2], [4, 5]] : staggered ? [[0, 3, 6, 1, 4, 5, 2]] : [[0, 3, 6, 1], [4, 5, 2]])
        .map(row => row.map(index => figures[index]).filter(Boolean))
      const cssScale = Number.parseFloat(getComputedStyle(container).getPropertyValue('--drawing-scale'))
      const preferredScale = mobile ? cssScale : Math.min(cssScale, .34)

      function footprints(scale) {
        container.style.setProperty('--drawing-scale', scale)
        return rows.map(row => row.map(element => {
          const rect = element.getBoundingClientRect()
          const image = element.querySelector('img').getBoundingClientRect()
          const captions = [...element.querySelectorAll('.drawing-caption, .drawing-caption span')]
            .map(caption => caption.getBoundingClientRect())
          const left = Math.min(rect.left, ...captions.map(caption => caption.left))
          const right = Math.max(rect.right, ...captions.map(caption => caption.right))
          const upper = Math.min(rect.top, ...captions.map(caption => caption.top))
          const lower = Math.max(rect.bottom, ...captions.map(caption => caption.bottom))
          const captionLeft = Math.min(...captions.map(caption => caption.left))
          const captionTop = Math.min(...captions.map(caption => caption.top))
          return {
            element, width: right - left, height: lower - upper, imageHeight: image.height,
            imageX: image.left - left, imageWidth: image.width,
            startX: left - bounds.left, startY: upper - bounds.top,
            captionX: captionLeft - left, captionY: captionTop - upper,
            captionWidth: Math.max(...captions.map(caption => caption.right)) - captionLeft,
            captionHeight: Math.max(...captions.map(caption => caption.bottom)) - captionTop,
          }
        }))
      }

      function fits(layout) {
        const height = layout.reduce((total, row, index) => {
          const rowHeight = Math.max(...row.map(item => item.height))
          const clipping = !mobile && !staggered && index === 0 ? Math.max(...row.map(item => item.imageHeight)) * 0.25 : 0
          return total + rowHeight - clipping + verticalDrift * 2
        }, minimumGap * (rows.length - 1) + edge * 2)
        return height <= referenceHeight && layout.every(row =>
          row.reduce((sum, item) => sum + item.width, 0) + (minimumGap + horizontalReserve * 2) * (row.length - 1) + edge * 2 <= sizingWidth)
      }

      // Fixed-width captions can consume most of a narrow row; retain the gap first.
      const minimumLayout = footprints(0)
      for (const row of minimumLayout) {
        if (row.length < 2) continue
        const occupied = row.reduce((sum, item) => sum + item.width, 0)
        horizontalReserve = Math.min(horizontalReserve, Math.max(0,
          (bounds.width - edge * 2 - occupied - minimumGap * (row.length - 1)) / (2 * (row.length - 1))))
      }
      let fittedScale = preferredScale
      let layout = footprints(fittedScale)
      if (!fits(layout)) {
        let low = 0
        let high = preferredScale
        for (let attempt = 0; attempt < 10; attempt++) {
          const scale = (low + high) / 2
          if (fits(footprints(scale))) low = scale
          else high = scale
        }
        fittedScale = low
      }
      // Retain the preceding fit calculation as the baseline, then double dimensions.
      let finalScale = Math.max(fittedScale * 2, 45 / 533)
      if (compressed && !mobile) {
        const sourceWidth = figures.reduce((sum, figure) => sum + Number(figure.querySelector('img').getAttribute('width')), 0)
        finalScale = Math.min(finalScale, (bounds.width - 28) / sourceWidth)
      }
      let enlarged = footprints(finalScale).flat()
      const mobileAnchors = [[0.04, 0.56], [0.15, 0.09], [0.93, 0.48], [0.52, 0.06], [0.13, 0.91], [0.86, 0.91], [0.88, 0.11]]
      const desktopAnchors = [[0.05, 0.42], [0.9, 0.17], [0.93, 0.72], [0.27, 0.12], [0.2, 0.88], [0.63, 0.91], [0.57, 0.08]]
      const anchors = mobileAnchors.map((anchor, index) => anchor.map((value, axis) =>
        value + (desktopAnchors[index][axis] - value) * layoutProgress))
      let ordered = [...enlarged].sort((a, b) => b.width * b.height - a.width * a.height)

      function arrange(visibleFraction) {
        const candidates = new Map()
        for (const item of ordered) {
          const anchor = anchors[figures.indexOf(item.element)]
          Object.assign(item, {
            minX: -item.width * (1 - visibleFraction),
            maxX: bounds.width - item.width * visibleFraction,
            minY: Math.min(top - item.imageHeight * (1 - visibleFraction), bottom - item.height - 4),
            maxY: bottom - item.height - 4,
          })
          item.minY = Math.max(item.minY, top - item.imageHeight + 32)
          item.maxY = Math.max(item.minY, item.maxY)
          // These two image corridors retain genuine clipping on both poster edges.
          const product = item.element.dataset.model
          if (product === 'TRV801' || item.element === figures[0]) {
            item.minX = -item.imageX - item.imageWidth * 0.28
            item.maxX = -item.imageX - item.imageWidth * 0.12
          } else if (product === 'TRV706' || item.element === figures[2]) {
            item.minX = bounds.width - item.imageX - item.imageWidth * 0.88
            item.maxX = bounds.width - item.imageX - item.imageWidth * 0.72
          }
          const desiredX = bounds.width * anchor[0] - item.width / 2
          const desiredY = top + (bottom - top) * anchor[1] - item.height / 2
          const positions = [{ x: clamp(desiredX, item.minX, item.maxX), y: clamp(desiredY, item.minY, item.maxY) }]
          for (let row = 0; row <= 8; row++) {
            for (let column = 0; column <= 12; column++) {
              positions.push({
                x: item.minX + (item.maxX - item.minX) * column / 12,
                y: item.minY + (item.maxY - item.minY) * row / 8,
              })
            }
          }
          positions.sort((a, b) =>
            Math.hypot(a.x - desiredX, a.y - desiredY) - Math.hypot(b.x - desiredX, b.y - desiredY))
          candidates.set(item, positions)
        }
        const placed = []
        let attempts = 0
        function place(index) {
          if (index === ordered.length) return true
          const item = ordered[index]
          for (const point of candidates.get(item)) {
            if (++attempts > 1800) return false
            const candidate = { ...item, ...point }
            if (blockedRegion(candidate, placed)) continue
            placed.push(candidate)
            if (place(index + 1)) {
              Object.assign(item, point)
              return true
            }
            placed.pop()
          }
          return false
        }
        return place(0)
      }

      // Establish the existing responsive scale before applying the requested enlargement.
      let arranged = false
      for (let attempt = 0; attempt < 8 && !arranged; attempt++) {
        for (const fraction of [0.67, 0.5, 0.4]) {
          if (arrange(fraction)) {
            arranged = true
            break
          }
        }
        if (!arranged && attempt < 7) {
          finalScale = Math.max(45 / 533, finalScale * 0.85)
          enlarged = footprints(finalScale).flat()
          ordered = [...enlarged].sort((a, b) => b.width * b.height - a.width * a.height)
        }
      }
      // Preserve both approved endpoints and scale continuously between them.
      if (bounds.width > 390) {
        const sourceWidths = figures.map(figure => Number(figure.querySelector('img').getAttribute('width')))
        const sourceHeights = figures.map(figure => Number(figure.querySelector('img').getAttribute('height')))
        const desktopScale = 1366 * .36 / Math.max(...sourceWidths)
        const originalRows = rows
        rows = [[1, 3, 6], [0, 2], [4, 5]].map(row => row.map(index => figures[index]).filter(Boolean))
        container.classList.add('hero-mobile-reference')
        const mobileHeight = Math.max(420, Number.parseFloat(getComputedStyle(hero).getPropertyValue('--hero-mobile-artwork-height')))
        const mobileFits = scale => {
          const layout = footprints(scale)
          const height = layout.reduce((sum, row) => sum + Math.max(...row.map(item => item.height)) + 6, 24)
          return height <= mobileHeight && layout.every(row =>
            row.reduce((sum, item) => sum + item.width, 0) + 18 * (row.length - 1) + 8 <= 390)
        }
        let low = 0
        let high = .115
        if (mobileFits(high)) low = high
        else for (let attempt = 0; attempt < 10; attempt++) {
          const scale = (low + high) / 2
          if (mobileFits(scale)) low = scale
          else high = scale
        }
        const mobileScale = Math.max(low * 2, 45 / 533)
        container.classList.remove('hero-mobile-reference')
        rows = originalRows
        finalScale = Math.min(mobileScale + (desktopScale - mobileScale) * layoutProgress,
          planeHeight * .72 / Math.max(...sourceHeights), desktopScale)
        enlarged = footprints(finalScale).flat()
        ordered = [...enlarged].sort((a, b) => b.width * b.height - a.width * a.height)
        arranged = false
        for (let attempt = 0; attempt < 12 && !arranged; attempt++) {
          for (const fraction of [0.67, 0.5, 0.4]) {
            if (arrange(fraction)) {
              arranged = true
              break
            }
          }
          if (!arranged && attempt < 11) {
            finalScale *= .9
            enlarged = footprints(finalScale).flat()
            ordered = [...enlarged].sort((a, b) => b.width * b.height - a.width * a.height)
          }
        }
      }
      if (!arranged) {
        // A last compact row stays finite even when external styles remove available height.
        const row = [figures[0], ...figures.filter(figure => figure !== figures[0] && figure !== figures[2]), figures[2]]
          .map(figure => enlarged.find(item => item.element === figure))
        const left = row[0].minX
        const right = row.at(-1).maxX + row.at(-1).width
        const gap = Math.max(0, (right - left - row.reduce((sum, item) => sum + item.width, 0)) / (row.length - 1))
        let x = left
        row.forEach(item => {
          item.x = clamp(x, item.minX, item.maxX)
          item.y = item.maxY
          x += item.width + gap
        })
      }
      fillLayer?.style.setProperty('--drawing-scale', finalScale)
      items = enlarged.map(item => {
        Object.assign(item, { vx: 0, vy: 0, fill: fillByProduct.get(item.element.dataset.model) })
        chooseTarget(item)
        if (!reducedMotion.matches) item.element.style.willChange = 'transform'
        if (!reducedMotion.matches && item.fill) item.fill.style.willChange = 'transform'
        paint(item)
        return item
      })
      updateRunning()
    }

    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      updateRunning()
    })
    intersectionObserver.observe(main)
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(container)
    resizeObserver.observe(main)
    figures.forEach(figure => figure.querySelectorAll('.drawing-caption span')
      .forEach(caption => resizeObserver.observe(caption)))
    const images = figures.map(figure => figure.querySelector('img'))
    images.forEach(image => image.addEventListener('load', measure))
    document.addEventListener('visibilitychange', updateRunning)
    reducedMotion.addEventListener('change', measure)
    measure()

    return () => {
      stop()
      intersectionObserver.disconnect()
      resizeObserver.disconnect()
      images.forEach(image => image.removeEventListener('load', measure))
      document.removeEventListener('visibilitychange', updateRunning)
      reducedMotion.removeEventListener('change', measure)
      container.style.removeProperty('--drawing-scale')
      fillLayer?.style.removeProperty('--drawing-scale')
      captions.forEach((caption, index) => { caption.style.width = captionWidths[index] })
      container.querySelectorAll('.drawing-description > span').forEach(caption => {
        caption.style.removeProperty('animation-play-state')
      })
      artworkLayers.forEach(figure => {
        figure.style.removeProperty('transform')
        figure.style.removeProperty('will-change')
      })
    }
  }, [ref])
}
